import "./global.css";
import "./tokens.css";
export * from "./shared";
export * from "./themes";

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DEFAULT_THEME_MODE, isThemeMode, ThemeMode } from "./themes";

declare module "react" {
  interface CSSProperties {
    // Catchall for CSS variables
    [key: `--${string}`]: string | number;
    //   Add custom CSS var definitions
    "--page_size"?: number;
    "--visible-columns-count"?: number;
    // Missing standard properties
    anchorName?: string;
  }
}

const STORAGE_KEY = "nazare-ui:theme-mode";

function readStoredMode(): ThemeMode {
  if (typeof window === "undefined") {
    return DEFAULT_THEME_MODE;
  }
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isThemeMode(stored) ? stored : DEFAULT_THEME_MODE;
}

function applyThemeAttribute(mode: ThemeMode) {
  if (typeof document === "undefined") {
    return;
  }
  const root = document.documentElement;
  if (mode === "system") {
    // No attribute → the `@media (prefers-color-scheme)` block in tokens.css
    // decides, i.e. "follow the OS".
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", mode);
  }
}

export type ThemeContextValue = {
  /** Current System / Light / Dark preference. */
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
};

export const Theme = createContext<ThemeContextValue>({
  mode: DEFAULT_THEME_MODE,
  setMode: () => null,
});

export const ThemeProvider = ({
  children,
  defaultMode,
  persist = true,
}: PropsWithChildren<{
  /** Initial mode. Falls back to the persisted preference, then "system". */
  defaultMode?: ThemeMode;
  /** Persist the chosen mode to localStorage so it survives reloads. */
  persist?: boolean;
}>) => {
  const [mode, setModeState] = useState<ThemeMode>(
    () => defaultMode ?? readStoredMode(),
  );

  useEffect(() => {
    applyThemeAttribute(mode);
    if (persist && typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, mode);
    }
  }, [mode, persist]);

  const setMode = useCallback((next: ThemeMode) => setModeState(next), []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, setMode }),
    [mode, setMode],
  );

  return <Theme.Provider value={value}>{children}</Theme.Provider>;
};

/**
 * Read/set the current System / Light / Dark preference set up by
 * `ThemeProvider`. See `design.md`'s "Dark mode & theming" section — this
 * hook only ever toggles the `data-theme` attribute; every component keeps
 * consuming the same themed CSS variables (tokens.css) either way.
 */
export const useTheme = () => useContext(Theme);
